import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { AlertTriangle, CreditCard, X } from "lucide-react";

interface PaymentRequiredBannerProps {
  className?: string;
}

const PaymentRequiredBanner: React.FC<PaymentRequiredBannerProps> = ({
  className = "",
}) => {
  const { t } = useTranslation("billing");
  const navigate = useNavigate();
  const location = useLocation();
  const { currentOrgSubscription, loading } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);

  const handleGoToBilling = () => {
    navigate("/account/billing");
  };

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  // Don't show on billing or onboarding pages to avoid redundancy
  if (
    location.pathname.includes("/account/billing") ||
    location.pathname.includes("/onboarding")
  ) {
    return null;
  }

  if (loading || isDismissed) {
    return null;
  }

  // Determine if payment is required and the appropriate message
  const subscription = currentOrgSubscription?.subscription;
  const needsPayment = currentOrgSubscription?.needsPayment ?? false;

  let errorMessage = "";

  if (!subscription) {
    if (!currentOrgSubscription) {
      return null; // No org context yet
    }
    errorMessage = t("no-subscription-payment-required");
  } else if (needsPayment) {
    const status = subscription.status;
    switch (status) {
      case "INCOMPLETE":
        errorMessage = t("incomplete-subscription-payment-required");
        break;
      case "INCOMPLETE_EXPIRED":
        errorMessage = t("incomplete-expired-subscription-payment-required");
        break;
      case "PAST_DUE":
        errorMessage = t("past-due-subscription-payment-required");
        break;
      case "UNPAID":
        errorMessage = t("unpaid-subscription-payment-required");
        break;
      default:
        errorMessage = t("payment-required-complete-subscription");
    }
  }

  if (!errorMessage) {
    return null;
  }

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[60] bg-red-600 text-white shadow-lg ${className}`}
      role="alert"
      aria-live="assertive"
      style={{ marginTop: 0 }}
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{errorMessage}</p>
              <p className="text-xs text-red-100 mt-0.5">
                {t("payment-required-action-message")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 ml-4 flex-shrink-0">
            <button
              onClick={handleGoToBilling}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-red-600 text-sm font-medium rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white transition-colors"
            >
              <CreditCard className="w-4 h-4" />
              {t("go-to-billing")}
            </button>
            <button
              onClick={handleDismiss}
              className="text-red-100 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white rounded-md p-1 transition-colors"
              aria-label={t("dismiss")}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequiredBanner;
